<script setup lang="ts">
import { computed, toRefs } from 'vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import { type CrmRelationsLoggingRelatieLog } from '@services/crm-public/orval-api/model/crmRelationsLoggingRelatieLog';
import { useRelatiesGetRelatieLogs } from '@services/crm-public/orval-api/relaties-api/relaties-api';
import { tryFormatDateTime } from '@tools/dates';
import VModal from '@shared/component/VModal/VModal.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';

const props = defineProps<{
  relatienummer: number;
  titel: string;
  omschrijving: string;
  filter?: (log: CrmRelationsLoggingRelatieLog) => boolean;
}>();

const isOpen = defineModel<boolean>('isOpen');

const { relatienummer } = toRefs(props);
const { data, isPending } = useRelatiesGetRelatieLogs(relatienummer, {
  query: { select: (d) => d.result ?? [] },
});

const logs = computed(() => {
  if (props.filter === undefined) {
    return data.value;
  }

  return data.value?.filter(props.filter);
});
</script>

<template>
  <VModal
    :is-active="isOpen"
    :title="titel"
    :invert-header="true"
    confirm-button-text=""
    size="sm"
    @close-modal="isOpen = false"
  >
    <VSkeleton height="lg" :count="3" v-if="isPending" />
    <VDataTable
      :value="logs"
      default-empty-title="Het logboek is leeg"
      :default-empty-description="omschrijving"
      :loading="isPending"
      sort-field="tijdstip"
      :removable-sort="false"
      :sort-order="-1"
      class="max-h-[40svh]"
      v-if="!isPending"
      searchable
    >
      <VColumn :sortable="true" header="Tijdstip" field="tijdstip">
        <template #body="{ data }">
          <span class="whitespace-nowrap">
            {{ tryFormatDateTime(data.tijdstip) }}
          </span>
        </template>
      </VColumn>
      <VColumn :sortable="true" header="Gebruiker" field="gebruikersnaam">
        <template #body="{ data }">
          <span class="whitespace-nowrap">
            {{ data.gebruikersnaam }}
          </span>
        </template>
      </VColumn>
      <VColumn header="Wijziging" field="logTekst">
        <template #body="{ data }">
          <span class="whitespace-pre">
            {{ data.logTekst }}
          </span>
        </template>
      </VColumn>
    </VDataTable>
  </VModal>
</template>
